import PropTypes from 'prop-types';
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';
import { render } from 'react-dom';
import { Form, Icon, Popup, Dropdown } from 'semantic-ui-react';
import { Field, reduxForm } from 'redux-form';
import * as formHelpers from '../../scripts/formHelpers';
import * as formValidators from '../../scripts/formValidators';
import {
  MotifPagination,
  MotifPaginationSelect,
  MotifPaginationSelectItem,
  MotifTable,
} from '@ey-xd/motif-react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import ExternalChecklistCheckboxCellRenderer from './customRenderers/ExternalChecklistCheckboxCellRenderer.js';
import ExternalChecklistCheckboxHeaderRenderer from './customRenderers/ExternalChecklistCheckboxHeaderRenderer.js';
import ReopentoRequestStatusValueGetter from './customValueGetters/ReopentoRequestStatusValueGetter.js';
import DateComparator from './customComparators/DateComparator.js';
import CustomHeaderTemplate from './customHeaders/CustomHeaderTemplate.js';
import ExternalChecklistLinkCellRenderer from './customRenderers/ExternalChecklistLinkCellRenderer';
import InternalRecipientsCellRenderer from '../shared/customRenderers/InternalRecipientsCellRenderer.js';

const PSQChecklistExternalTable = ({
  formSyncErrors,
  formSubmitErrors,
  hasWriteAccess,
  filteredExternalChecklists,
  clientId,
  selectedClient,
  gridColumnState,
  handleGridColumnStateChange,
  handleGridChanges,
  refreshGridOnCompletedAction,
}) => {
  const gridRef = useRef();
  const containerStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);
  const gridStyle = useMemo(() => ({ height: '100%', width: '100%' }), []);

  const [rowData, setRowData] = useState([]);
  const rowDataRef = useRef({});
  rowDataRef.current = rowData;

  const onHeaderCheckboxChange = useCallback((isHeaderCheckboxSelected) => {
    let updatedData = rowDataRef.current;
    if (gridRef && gridRef.current && gridRef.current.api) {
      // Select checkbox for filtered rows only
      gridRef.current.api.forEachNodeAfterFilter((rowNode) => {
        // Check if the parent rowNode's key matches with grid's rowdata and update checkbox selection for those matching
        if (rowNode.level === 0) {
          updatedData = updatedData.map((newUd) => {
            if (newUd.checklistID === rowNode.data.checklistID) {
              return {
                ...newUd,
                checklistSelection: !isHeaderCheckboxSelected,
              };
            }
            return newUd;
          });
        }
      });
      setRowData(updatedData);
      gridRef.current.api.refreshHeader();
    }
  }, []);

  const onCellCheckboxChange = useCallback((checklistId, rowNode) => {
    let updatedData = rowDataRef.current.map((newUd) => {
      if (newUd.checklistID === checklistId) {
        return {
          ...newUd,
          checklistSelection: !newUd.checklistSelection,
        };
      }
      return newUd;
    });

    setRowData(updatedData);
    gridRef.current.api.refreshHeader();
  }, []);

  //Default column settings
  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      minWidth: 65,
      filter: true,
      resizable: true,
      sortable: true,
      wrapText: true,
      autoHeight: true,
    };
  }, []);

  const dateOptions = useMemo(
    () => ({
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }),
    []
  );

  const columnDefs = useMemo(
    () => [
      {
        field: 'checklistSelection',
        headerName: '',
        suppressMenu: true,
        suppressHeaderFilterButton: true,
        minWidth: 65,
        headerComponent: ExternalChecklistCheckboxHeaderRenderer,
        headerComponentParams: {
          onHeaderCheckboxChange: onHeaderCheckboxChange,
        },
        cellRendererParams: { onCellCheckboxChange: onCellCheckboxChange },
        cellRenderer: ExternalChecklistCheckboxCellRenderer,
      },
      {
        field: 'checklistLink',
        headerName: '',
        suppressMenu: true,
        suppressHeaderFilterButton: true,
        minWidth: 54,
        maxWidth: 54,
        cellRenderer: ExternalChecklistLinkCellRenderer,
        cellRendererParams: { clientId: clientId },
        headerComponentParams: {
          template: CustomHeaderTemplate('checklistLink'),
        },
      },
      {
        field: 'quarter',
        headerName: 'Period',
        sortable: true,
        showRowGroup: false,
        rowGroup: false,
        minWidth: 140,
        maxWidth: 140,
        valueGetter: (params) => {
          return params.node && params.node.data
            ? params.node.data['quarter']
            : '';
        },
        filterValueGetter: (params) => {
          return params.data && params.data.quarter;
        },
        headerComponentParams: { template: CustomHeaderTemplate('period') },
      },
      {
        field: 'reitName',
        headerName: 'REIT',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 225,
        valueGetter: (params) => {
          return params.node && params.node.data
            ? params.node.data['reitName']
            : '';
        },
        filterValueGetter: (params) => {
          return params.data && params.data.reitName;
        },
        headerComponentParams: { template: CustomHeaderTemplate('reit') },
      },
      {
        field: 'propertyName',
        headerName: 'Property',
        sortable: true,
        minWidth: 225,
        valueGetter: (params) => {
          return params.node && params.node.data
            ? params.node.data['propertyName']
            : '';
        },
        filterValueGetter: (params) => {
          return params.data && params.data.propertyName;
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('propertyName'),
        },
      },
      {
        field: 'propertyTypeDescription',
        headerName: 'Property Type',
        sortable: true,
        minWidth: 190,
        valueGetter: (params) => {
          return params.node && params.node.data
            ? params.node.data['propertyTypeDescription']
            : '';
        },
        filterValueGetter: (params) => {
          return params.data && params.data.propertyTypeDescription;
        },
        headerComponentParams: { template: CustomHeaderTemplate('type') },
      },
      {
        field: 'checklistStateDescription',
        headerName: 'Status',
        sortable: true,
        showRowGroup: false,
        hide: false,
        rowGroup: false,
        minWidth: 210,
        valueGetter: (params) => {
          return params.node && params.node.data
            ? params.node.data['checklistStateDescription']
            : '';
        },
        filterValueGetter: (params) => {
          return params.data && params.data.checklistStateDescription;
        },
        headerComponentParams: { template: CustomHeaderTemplate('status') },
      },
      {
        field: 'lastUpdatedDate',
        headerName: 'Status Date',
        sortable: true,
        minWidth: 180,
        valueGetter: (params) => {
          return params.node && params.node.data
            ? params.node.data['lastUpdatedDate']
              ? new Date(
                  params.node.data['lastUpdatedDate'] + 'Z'
                ).toLocaleString('en-US', dateOptions)
              : ''
            : null;
        },
        filterValueGetter: (params) => {
          return (
            params.data &&
            new Date(params.data.lastUpdatedDate + 'Z').toLocaleString(
              'en-US',
              dateOptions
            )
          );
        },
        comparator: DateComparator,
        headerComponentParams: { template: CustomHeaderTemplate('statusDate') },
      },
      {
        field: 'internalRecipients',
        headerName: 'Internal Review Recipients',
        sortable: true,
        showRowGroup: false,
        rowGroup: false,
        minWidth: 270,
        valueGetter: (params) => {
          return params.node && params.node.data
            ? params.node.data['internalRecipients']
            : '';
        },
        cellRenderer: InternalRecipientsCellRenderer,
        cellRendererParams: { fieldName: 'internalRecipients' },
        filterValueGetter: (params) => {
          return params.data && params.data.internalRecipients;
        },
        headerComponentParams: {
          template: CustomHeaderTemplate('internalRecipients'),
        },
      },
      {
        field: 'reopenRequestStatusDescription',
        headerName: 'Reopen Request',
        sortable: true,
        minWidth: 203,
        valueGetter: (params) => ReopentoRequestStatusValueGetter(params),
        cellRenderer: (params) => {
          return params.data && params.data.reopenRequestStatusDescription
            ? params.data.reopenRequestStatusDescription.toLowerCase() ===
              'pending'
              ? 'Pending EY Action'
              : params.data.reopenRequestStatusDescription
            : 'N/A';
        },
        filterValueGetter: (params) => {
          return params.data && params.data.reopenRequestStatusDescription
            ? params.data.reopenRequestStatusDescription.toLowerCase() ===
              'pending'
              ? 'Pending EY Action'
              : params.data.reopenRequestStatusDescription
            : 'N/A';
        },

        headerComponentParams: {
          template: CustomHeaderTemplate('reopenRequestStatusDescription'),
        },
      },
      {
        field: 'requestDateTime',
        headerName: 'Reopen Request Date',
        sortable: true,
        minWidth: 240,
        valueGetter: (params) => {
          return params.node && params.node.data
            ? params.node.data['requestDateTime']
              ? new Date(
                  params.node.data['requestDateTime'] + 'Z'
                ).toLocaleString('en-US', dateOptions)
              : ''
            : null;
        },
        filterValueGetter: (params) => {
          return params.data
            ? params.data['requestDateTime']
              ? new Date(params.data.requestDateTime + 'Z').toLocaleString(
                  'en-US',
                  dateOptions
                )
              : 'N/A'
            : null;
        },
        cellRenderer: (params) => {
          return params.data && params.data.requestDateTime
            ? new Date(params.data.requestDateTime + 'Z').toLocaleString(
                'en-US',
                dateOptions
              )
            : 'N/A';
        },
        comparator: DateComparator,
        headerComponentParams: {
          template: CustomHeaderTemplate('requestDateTime'),
        },
      },
      {
        field: 'clientUserEmailAddress',
        headerName: 'Client Signedoff By',
        sortable: true,
        minWidth: 225,
        valueGetter: (params) => {
          return params.node && params.node.data && params.node.data.isSignedOff
            ? params.node.data.clientUserEmailAddress
            : '';
        },
        cellRenderer: (params) => {
          return params.data && params.data.isSignedOff
            ? params.data.clientUserEmailAddress
            : '';
        },
        filterValueGetter: (params) => {
          return params.data && params.data.isSignedOff
            ? params.data.clientUserEmailAddress
            : '';
        },

        headerComponentParams: {
          template: CustomHeaderTemplate('clientUserEmailAddress'),
        },
      },
      {
        field: 'userActionDateTime',
        headerName: 'Client Signedoff Date',
        sortable: true,
        minWidth: 250,
        valueGetter: (params) => {
          return params.node && params.node.data && params.node.data.isSignedOff
            ? params.node.data.userActionDateTime
              ? new Date(
                  params.node.data.userActionDateTime + 'Z'
                ).toLocaleString('en-US', dateOptions)
              : ''
            : null;
        },
        filterValueGetter: (params) => {
          return params.data && params.data.isSignedOff
            ? params.data.userActionDateTime
              ? new Date(params.data.userActionDateTime + 'Z').toLocaleString(
                  'en-US',
                  dateOptions
                )
              : 'N/A'
            : null;
        },
        cellRenderer: (params) => {
          return params.data &&
            params.data.isSignedOff &&
            params.data.userActionDateTime
            ? new Date(params.data.userActionDateTime + 'Z').toLocaleString(
                'en-US',
                dateOptions
              )
            : '';
        },
        comparator: DateComparator,
        headerComponentParams: {
          template: CustomHeaderTemplate('userActionDateTime'),
        },
      },
    ],
    [onHeaderCheckboxChange, onCellCheckboxChange, clientId, dateOptions]
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);

  //Grid Global Listner
  const gridGlobalListner = useCallback(
    function (type, event) {
      if (type.indexOf('columnVisible') >= 0) {
        handleGridColumnsChanged(event);
      }

      // Bind handleFilterChanged event to filterChanged and rowDataUpdated events
      if (type.indexOf('filterChanged') >= 0) {
        setTimeout(function () {
          handleFilterChanged(event);
        }, 0);
      }

      if (type.indexOf('rowDataUpdated') >= 0) {
        setTimeout(function () {
          handleFilterChanged(event);
        }, 0);
      }
    },
    [handleGridColumnsChanged, handleFilterChanged]
  );

  //Handle Grid Ready event
  const handleGridReady = useCallback(
    (event) => {
      //Remove event for column state change
      event.api.removeEventListener(gridGlobalListner);

      //Add event for column state change
      event.api.addGlobalListener(gridGlobalListner);

      //Apply column state
      var columnState = localStorage.getItem(
        'REITSuiteExternalPSQChecklistColumnState'
      );
      if (columnState) {
        gridRef.current.columnApi.applyColumnState({
          state: JSON.parse(columnState),
          applyOrder: true,
        });
      }

      // Set the page size and total pages on the first load
      // Setting it 100 to make sure the default number of items on load is always set to 100
      handleItemsPerPageChange(100);
    },
    [gridGlobalListner, handleItemsPerPageChange]
  );

  // Handle OnFilterChanged event
  const handleFilterChanged = useCallback(
    (event) => {
      if (gridRef && gridRef.current && gridRef.current.api) {
        let calcTotalPages = gridRef.current.api.paginationGetTotalPages();
        setTotalPages(calcTotalPages);
        if (currentPage > calcTotalPages) {
          if (calcTotalPages == 0) {
            setTimeout(function () {
              setCurrentPage(calcTotalPages);
            }, 0);
          } else {
            setCurrentPage(calcTotalPages);
          }
        }
        const gridCurrentPage =
          gridRef.current.api.paginationGetCurrentPage() + 1; // Adding 1 since it returns zero based index
        if (currentPage < gridCurrentPage && calcTotalPages != 0) {
          setCurrentPage(gridCurrentPage);
        }

        // Show the expected value as 1 for current page
        if (currentPage <= 1 && gridCurrentPage == 1 && calcTotalPages != 0) {
          setCurrentPage(gridCurrentPage);
        }
      }
    },
    [currentPage]
  );

  //Handle First Data Renderered event
  const handleFirstDataRendered = useCallback((event) => {
    gridRef.current.columnApi.autoSizeAllColumns();
  }, []);

  //Save User Preference details (Column state)
  const saveUserPreferenceDetails = useCallback(() => {
    var columnState = gridRef.current.columnApi.getColumnState();
    if (columnState) {
      localStorage.setItem(
        'REITSuiteExternalPSQChecklistColumnState',
        JSON.stringify(columnState)
      );
    }
  }, []);

  //Handle Grid Columns changed event
  const handleGridColumnsChanged = useCallback(
    (event) => {
      var currentColumnState = gridRef.current.columnApi.getColumnState();
      var localStorageColumnState = localStorage.getItem(
        'REITSuiteExternalPSQChecklistColumnState'
      );

      // If the column state details are not exists in local storage then save the changed Column state data
      if (
        currentColumnState &&
        currentColumnState.filter((col) => col.hide == true)?.length > 0 &&
        !localStorageColumnState
      ) {
        saveUserPreferenceDetails();
      } else if (localStorageColumnState) {
        // If the column state details are exists in local storage then save the changed Column state data only if difference is found
        var columnsStateArray = JSON.parse(localStorageColumnState);
        if (
          columnsStateArray &&
          columnsStateArray.length > 0 &&
          currentColumnState &&
          currentColumnState.length > 0 &&
          columnsStateArray.filter((col) => col.hide == true)?.length !=
            currentColumnState.filter((col) => col.hide == true)?.length
        ) {
          //If column state mismatch found then only save column state data to avoid saving same state
          saveUserPreferenceDetails();
        }
      }

      //Trigger Grid Column state change (of parent) to sync column state data
      handleGridColumnStateChange(currentColumnState);

      gridRef &&
        gridRef.current &&
        gridRef.current.columnApi &&
        gridRef.current.columnApi.autoSizeAllColumns();
    },
    [handleGridColumnStateChange, saveUserPreferenceDetails]
  );

  //Handle Grid Items per Page change event
  const handleItemsPerPageChange = useCallback((val) => {
    gridRef.current.api.paginationGoToFirstPage();
    gridRef.current.api.setGridOption('paginationPageSize', Number(val));
    setCurrentPage(1);
    setItemsPerPage(val);
    // Get total pages from Grid's default pagination control and apply it to custom pagination control
    if (gridRef && gridRef.current && gridRef.current.api) {
      let calcTotalPages = gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);
      //If total page is zero then set the current page as zero
      if (calcTotalPages == 0) {
        setCurrentPage(calcTotalPages);
      }
    }
  }, []);

  //Handle Grid Page change event
  const handlePageChange = useCallback((val) => {
    setCurrentPage(val);
    gridRef.current.api.paginationGoToPage(val - 1);
  }, []);

  useEffect(() => {
    handleGridChanges(
      rowData,
      gridRef && gridRef.current ? gridRef.current.api : null
    );
  }, [rowData]);

  useEffect(() => {
    // Refresh Grid
    const gridApi = gridRef && gridRef.current && gridRef.current.api;
    if (gridApi) {
      gridApi.refreshCells();
      gridApi.deselectAll();
    }
  }, [refreshGridOnCompletedAction]);

  // Handle Row data updated event
  const handleRowDataUpdated = useCallback(() => {
    if (gridRef && gridRef.current && gridRef.current.columnApi) {
      gridRef.current.columnApi.autoSizeAllColumns();
    }
  }, []);

  useEffect(() => {
    setRowData(filteredExternalChecklists);

    if (gridRef && gridRef.current && gridRef.current.api) {
      gridRef.current.api.refreshHeader();
      // Get total pages from Grid's default pagination control and apply it to custom pagination control
      let calcTotalPages = gridRef.current.api.paginationGetTotalPages();
      setTotalPages(calcTotalPages);
    }

    const gridApi = gridRef?.current?.api;
    gridApi && gridApi.onFilterChanged();
  }, [filteredExternalChecklists]);

  const handleSelectionChanged = useCallback((event) => {}, []);

  const rowHeight = 76;
  const headerHeight = 45;
  const totalHeight = 5 * rowHeight + headerHeight; // set 5 rows as default

  return (
    <div className="row mb-3">
      {filteredExternalChecklists && filteredExternalChecklists.length > 0 && (
        <div className="col mt-3">
          <div
            className="ag-theme-quartz motif-table table-bd-full-height-width"
            style={{ marginTop: '50px' }}
          >
            <MotifTable
              zebra={true}
              compact={true}
              rowData={rowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              accentedSort={true}
              rowSelection="multiple"
              suppressRowClickSelection={true}
              ref={gridRef}
              pagination={true}
              suppressPaginationPanel={true}
              paginationPageSize="100"
              groupDisplayType={'custom'}
              groupSelectsChildren={true}
              groupDefaultExpanded={0}
              onFirstDataRendered={handleFirstDataRendered}
              onSelectionChanged={handleSelectionChanged}
              onGridReady={handleGridReady}
              onFilterChanged={handleFilterChanged}
              onRowDataUpdated={handleRowDataUpdated}
              data-testid="externalChecklistGrid"
            />
            <div className="mt-3 mb-3 right">
              <MotifPagination
                currentPage={currentPage}
                onPageChange={handlePageChange}
                min={1}
                max={totalPages}
              >
                <MotifPaginationSelect>
                  <MotifPaginationSelectItem
                    onClick={() => handleItemsPerPageChange(50)}
                  >
                    Show 50
                  </MotifPaginationSelectItem>
                  <MotifPaginationSelectItem
                    selected
                    onClick={() => handleItemsPerPageChange(100)}
                  >
                    Show 100
                  </MotifPaginationSelectItem>
                  <MotifPaginationSelectItem
                    onClick={() => handleItemsPerPageChange(150)}
                  >
                    Show 150
                  </MotifPaginationSelectItem>
                  <MotifPaginationSelectItem
                    onClick={() => handleItemsPerPageChange(200)}
                  >
                    Show 200
                  </MotifPaginationSelectItem>
                </MotifPaginationSelect>
              </MotifPagination>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

PSQChecklistExternalTable.propTypes = {};

export default reduxForm({ form: 'psqChecklistExternal' })(
  PSQChecklistExternalTable
);
